import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import {
  barFraction,
  buildSeries,
  categoryTotals,
  countsFromOrders,
  fmt,
  maxBucketRevenue,
  maxCategoryOrders,
  maxItemOrders,
  ordersBetween,
  periodRange,
  toDateInput,
  rankItems,
  summarise,
  useAppStore,
} from '@restorant/core';
import type { DateRange, Period } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useCatLabel, useT } from '../i18n';
import { useCategories, useItemName, useMenuItems } from '../menu';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { Screen } from '../components/Screen';
import { BarChart } from '../components/BarChart';
import { BarRow, BarTrack } from '../components/BarRow';
import { EmptyNote } from '../components/EmptyNote';
import { Panel } from '../components/Panel';
import { SectionTitle } from '../components/SectionTitle';
import { StatTile } from '../components/StatTile';
import { T } from '../components/Text';
import {
  PeriodTabs, RangePicker, daysAgo, usePeriodLabel,
} from '../components/PeriodPicker';

export function AnalyticsScreen({ contentPadding }: { contentPadding: number }) {
  const colors = useColors();
  const t = useT();
  const catLabel = useCatLabel();
  const itemName = useItemName();
  const periodLabel = usePeriodLabel();
  const orders = useAppStore((s) => s.orders);
  const [period, setPeriod] = useState<Period>('day');
  // Defaults to the last week so the custom tab is never empty on first open.
  const [range, setRange] = useState<DateRange>(() => ({
    start: daysAgo(6),
    end: toDateInput(new Date()),
  }));

  // Everything below is scoped to the chosen period. `now` is captured once per
  // render so the counters, the chart and the rankings cannot disagree by a
  // millisecond across a midnight boundary.
  const { inPeriod, series, seriesMax, totalsForPeriod } = useMemo(() => {
    const now = new Date();
    const { from, to } = periodRange(period, now, range);
    const scoped = ordersBetween(orders, from, to);
    const buckets = buildSeries(scoped, period, now, range);
    return {
      inPeriod: scoped,
      series: buckets,
      seriesMax: maxBucketRevenue(buckets),
      totalsForPeriod: summarise(scoped),
    };
  }, [orders, period, range]);

  // Rankings follow the period too — "best selling" over all time is a
  // different question from "what sold today", and the tab says today.
  const orderCounts = useMemo(() => countsFromOrders(inPeriod), [inPeriod]);
  const categories = useCategories();
  const menuItems = useMenuItems();

  const ranked = rankItems(menuItems, orderCounts, true);
  const max = maxItemOrders(menuItems, orderCounts);
  const totals = categoryTotals(categories, menuItems, orderCounts);
  const catMax = maxCategoryOrders(totals);

  return (
    <Screen contentPadding={contentPadding}>
      <PeriodTabs period={period} onChange={setPeriod} />
      {period === 'custom' ? <RangePicker range={range} onChange={setRange} /> : null}

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        <StatTile
          value={String(totalsForPeriod.orders)}
          label={t('orders_in_period')}
          icon="order"
        />
        <StatTile value={fmt(totalsForPeriod.revenue)} label={t('stat_revenue')} icon="rupee" />
        <StatTile
          value={String(totalsForPeriod.items)}
          label={t('items_in_period')}
          icon="menu"
        />
        <StatTile value={fmt(totalsForPeriod.average)} label={t('avg_order')} icon="analytics" />
      </View>

      <SectionTitle title={t('revenue_over_time')} icon="analytics" />
      <BarChart
        buckets={series}
        max={seriesMax}
        emptyLabel={t('no_sales_in_period')}
        periodLabel={periodLabel(period, range)}
        ordersLabel={(n) => `${n} ${t('orders_word')}`}
      />

      <SectionTitle title={t('best_selling')} icon="flame" />
      <Panel>
        {ranked.length ? (
          ranked.map((r) => (
            <BarRow
              key={r.item.id}
              name={itemName(r.item)}
              fraction={barFraction(r.orders, max)}
              count={r.orders}
            />
          ))
        ) : (
          <EmptyNote>{t('no_sales_in_period')}</EmptyNote>
        )}
      </Panel>

      <SectionTitle title={t('by_category')} icon="analytics" />
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        {totals.map((c) => (
          <View
            key={c.cat.id}
            style={{
              flexGrow: 1,
              flexBasis: 210,
              backgroundColor: colors.surface2,
              borderWidth: 1,
              borderColor: colors.line,
              borderRadius: radius.md,
              paddingVertical: 13,
              paddingHorizontal: 14,
              gap: 8,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
              <Icon name={c.cat.icon as IconName} size={16} color={colors.gold} />
              <T size={13} weight={700} numberOfLines={1} style={{ flexShrink: 1 }}>
                {catLabel(c.cat.id)}
              </T>
            </View>
            <BarTrack fraction={barFraction(c.orders, catMax)} />
            <T size={11.5} color={colors.textFaint}>
              {c.orders} {t('orders_word')} · {c.itemCount} {t('items_on_menu')}
            </T>
          </View>
        ))}
      </View>
    </Screen>
  );
}
